import { classNames } from "@circle-react/helpers/twMergeWithCN";

import { t } from "@/i18n-js/instance";

interface DefaultTermsLabelProps {
  communityPrivacyUrl?: string;
  communityTermsUrl?: string;
  locale?: "pt" | "en";
  showCreatorTerms?: boolean;
  useShortCopy?: boolean;
  variant: "paywallCheckout" | "newPaywallCheckout" | "default";
}

const defaultStyle = {
  spanWrapperClassName: "text-light text-xs",
  firstLinkClassName: "text-default hover:text-light font-bold",
  secondLinkClassName: "text-default hover:text-light font-bold",
};

const variants = {
  default: defaultStyle,
  paywallCheckout: defaultStyle,
  newPaywallCheckout: {
    spanWrapperClassName:
      "text-xs font-normal leading-5 tracking-tight normal-case text-primary",
    firstLinkClassName: "text-dark hover:underline hover:text-dark font-bold",
    secondLinkClassName: "text-dark hover:underline hover:text-dark font-bold",
  },
};

const CIRCLE_BASE_PATH = "https://circle.so";

const termsLabelTranslations = {
  default: {
    creator: () =>
      t(
        "authentication_app.signup.form.terms_and_privacy.creator_terms_of_service",
      ),
    terms: () =>
      t("authentication_app.signup.form.terms_and_privacy.terms_of_service"),
  },
  short: {
    creator: () =>
      t(
        "authentication_app.signup.form.terms_and_privacy_otp_first.creator_terms_of_service",
      ),
    terms: () =>
      t(
        "authentication_app.signup.form.terms_and_privacy_otp_first.terms_of_service",
      ),
  },
};

const createUrl = (path: string, locale?: "pt" | "en") => {
  const localPrefix = locale === "pt" ? "/br" : "";
  return `${CIRCLE_BASE_PATH}${localPrefix}/${path}`;
};

export const DefaultTermsLabel = ({
  showCreatorTerms,
  locale,
  communityTermsUrl,
  communityPrivacyUrl,
  useShortCopy,
  variant,
}: DefaultTermsLabelProps) => {
  const variantStyle = variants[variant] || variants["default"];
  const copyTranslations = useShortCopy
    ? termsLabelTranslations.short
    : termsLabelTranslations.default;
  const termsLabel =
    (showCreatorTerms
      ? copyTranslations.creator?.()
      : copyTranslations.terms?.()) ??
    t("authentication_app.signup.form.terms_and_privacy.terms_of_service");

  const termsUrl = showCreatorTerms
    ? createUrl("creator-terms", locale)
    : communityTermsUrl;
  const privacyUrl = showCreatorTerms
    ? createUrl("privacy", locale)
    : communityPrivacyUrl;

  return (
    <span className={classNames(variantStyle.spanWrapperClassName)}>
      {useShortCopy
        ? t("authentication_app.signup.form.terms_and_privacy_otp_first.prefix")
        : t("authentication_app.signup.form.terms_and_privacy.prefix")}{" "}
      <a
        className={classNames(variantStyle.firstLinkClassName)}
        href={termsUrl}
        target="_blank"
        rel="noreferrer"
      >
        {termsLabel}
      </a>{" "}
      {useShortCopy
        ? t("authentication_app.signup.form.terms_and_privacy_otp_first.and")
        : t("authentication_app.signup.form.terms_and_privacy.and")}{" "}
      <a
        className={classNames(variantStyle.secondLinkClassName)}
        href={privacyUrl}
        target="_blank"
        rel="noreferrer"
      >
        {useShortCopy
          ? t(
              "authentication_app.signup.form.terms_and_privacy_otp_first.privacy_policy",
            )
          : t(
              "authentication_app.signup.form.terms_and_privacy.privacy_policy",
            )}
      </a>
    </span>
  );
};
